import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { AllSessions, CSVSession, HostSettings, Session } from './types'
import { extractXmlError } from '../utils/helpers'

const baseQuery = fetchBaseQuery({
  baseUrl: `/api/`,
})

export const appApi = createApi({
  reducerPath: 'appApi',
  baseQuery,
  tagTypes: ['HostSettings', 'AllSessions'], // these help invalidate data in the store to trigger auto-refetch when a mutation would invalidate the cache for a query
  endpoints: (builder) => ({
    getSession: builder.query<Session, string>({
      // pass through the s3 bucket url
      query: (url) => url,
      // any error returned from s3 is in xml and rtk query throws an error attempting to JSON.parse()
      // extract the source itself and parse to obtain code
      transformErrorResponse: ({ data }) => extractXmlError(data as string),
    }),
    updateSession: builder.mutation<
      Session,
      { session: Session; oldGroupId?: string }
    >({
      query: ({ session, oldGroupId }) => ({
        url: `sessions/update`,
        method: 'POST',
        body: { session, oldGroupId },
      }),
    }),
    exportCSV: builder.mutation<string, CSVSession>({
      query: (payload) => ({
        url: '/csv',
        method: 'POST',
        body: payload,
      }),
    }),
    createSession: builder.mutation<Required<Session>, Session>({
      query: (session) => ({
        url: 'sessions/create',
        method: 'POST',
        body: { session },
      }),
      invalidatesTags: ['AllSessions'],
    }),
    deleteSession: builder.mutation<
      { wasActiveSession: boolean; id: string; groupId: string },
      Pick<Session, 'groupId' | 'id'>
    >({
      query: ({ groupId, id }) => ({
        url: `sessions/delete`,
        method: 'POST',
        body: { session: { id, groupId } },
      }),
    }),
    getAllSessions: builder.query<AllSessions, string>({
      query: () => ({
        url: `sessions/`,
      }),
      providesTags: ['AllSessions'],
    }),
    showBadges: builder.mutation<string, HostSettings['showBadges']>({
      query: (showBadges) => ({
        url: 'hostSettings/update',
        method: 'POST',
        body: { showBadges },
      }),
    }),
    showFTE: builder.mutation<string, HostSettings['showFTE']>({
      query: (showFTE) => ({
        url: 'hostSettings/update',
        method: 'POST',
        body: { showFTE },
      }),
      invalidatesTags: ['HostSettings'],
    }),
    getHostSettings: builder.query<HostSettings, string>({
      query: () => ({ url: 'hostSettings' }),
      providesTags: ['HostSettings'],
    }),
    endActiveSession: builder.mutation<Session, void>({
      query: () => ({ url: 'sessions/end-active', method: 'POST' }),
      invalidatesTags: ['AllSessions'],
    }),
    getNewSessionSignedUrl: builder.query<string, string>({
      query: (expiredUrl) => ({
        url: `sessions/new-signed-url`,
        method: 'POST',
        body: { expiredUrl },
      }),
    }),
    createGroup: builder.mutation<
      {
        groupNames: HostSettings['groupNames']
        groupIds: HostSettings['groupIds']
        newGroupId: string
      },
      string
    >({
      query: (groupName) => ({
        url: 'groups/create',
        method: 'POST',
        body: { groupName },
      }),
      // TODO: optimize to invalidate only if the group created is being applied to a session immediately
      invalidatesTags: ['AllSessions'],
    }),
    updateGroup: builder.mutation<
      {
        groupNames: HostSettings['groupNames']
        groupIds: HostSettings['groupIds']
        oldGroupId: string
        newGroupId?: string
      },
      { oldGroupId: string; newGroupId?: string; newGroupName?: string }
    >({
      query: ({ oldGroupId, newGroupId, newGroupName }) => ({
        url: 'groups/update',
        method: 'POST',
        body: { oldGroupId, newGroupId, newGroupName },
      }),
      // TODO: optimize to invalidate only if the group has sessions in allSessions
      invalidatesTags: ['AllSessions'],
    }),
    deleteGroup: builder.mutation<
        {
          groupNames: HostSettings['groupNames']
          groupIds: HostSettings['groupIds']
        },
        {groupId: string }
    >({
      query: ({ groupId }) => ({
        url: `groups/delete`,
        method: 'POST',
        body: { groupId },
      }),
      // TODO: optimize to invalidate only if the group has sessions in allSessions
      invalidatesTags: ['AllSessions','HostSettings'],
    }),
    revisitHome: builder.query({
      query: (zoomAppContext) => ({
        url: '/zoomapp/home',
        method: 'GET',
        headers: { 'X-Zoom-App-Context': zoomAppContext },
        responseHandler: 'text',
      }),
    }),
  }),
})

export const {
  useGetSessionQuery,
  useUpdateSessionMutation,
  useExportCSVMutation,
  useCreateSessionMutation,
  useShowBadgesMutation,
  useShowFTEMutation,
  useGetHostSettingsQuery,
  useDeleteSessionMutation,
  useLazyGetNewSessionSignedUrlQuery,
  useEndActiveSessionMutation,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useDeleteGroupMutation,
  useGetAllSessionsQuery,
  useLazyRevisitHomeQuery,
} = appApi
